// module.forecast (contract §4.8.1): ForecastModule (OutlookModules.tsx): the
// model's wind (dashed grey) and gusts (dotted, stepped) from two hours ago to
// the horizon on one zero-based scale, the chance of rain as a strip of thin
// columns along the bottom on its own scale, the "now" cursor, the time axis,
// and the model's honesty line; or the module's line while the forecast is
// pending or unavailable.
//
// Where the page differs from today's module: the peak gust, its time and the
// squall risk ride the module card's header today; the page shows them again
// as a line over the chart, since a value field is shown on the page (§9.6).
// The strip of wind direction arrows is not drawn: the feed carries no
// direction.

import { css, h, n } from "./kit.js";
import { F, R, S } from "./module-kit.js";

const M = { top: 18, right: 12, bottom: 22, left: 42 };
/** The rain strip under the lines, and its gap (HistoryChart's least strip). */
const STRIP = 28;
const GAP = 8;
const HOURS = 48;

export function moduleForecast(spec) {
  const horizon =
    typeof spec.config?.horizonHours === "number" &&
    spec.config.horizonHours > 0
      ? spec.config.horizonHours
      : 24;
  const nowFrac = 2 / (2 + horizon);
  const lineInset = `${M.top} ${M.right} ${M.bottom + STRIP + GAP} ${M.left}`;
  const line = {
    "data-tc-y": "feed",
    "data-tc-group": "w",
    "data-tc-inset": lineInset,
  };
  const parts = [];
  for (let k = 0; k < 6; k += 1)
    parts.push(
      h("line", { class: "g", x2: 1, "data-tc-scale": "w", "data-tc-tick": k }),
      h("text", { class: "t", "data-tc-scale": "w", "data-tc-tick": k }),
    );
  parts.push(h("line", { class: "b", x2: 1 }));
  for (let k = 0; k < 8; k += 1)
    parts.push(h("text", { class: "x", "data-tc-time-tick": k }));
  for (let k = 0; k < HOURS; k += 1)
    parts.push(
      h("rect", { class: "rn", width: 1, height: 1, ...R("hour-rain", k) }),
    );
  parts.push(
    h("path", { class: "l lw", ...S("wind", line) }),
    h("path", {
      class: "l lg",
      ...S("gusts", { ...line, "data-tc-curve": "step" }),
    }),
    h("line", { class: "nw", y2: 1 }),
    h("text", { class: "nl" }, "now"),
    h("circle", { class: "pk", r: 4, ...F("peak") }),
  );
  const pw = `(var(--tc-w,320) - ${M.left + M.right})`;
  const ph = `(var(--tc-h,168) - ${M.top + M.bottom + STRIP + GAP})`;
  const x = (f) => `calc(${M.left}px + ${f} * ${pw} * 1px)`;
  return {
    kind: true,
    html: [
      h("i", { class: "mo-flag", ...F("state") }),
      h("p", { class: "mo-msg", ...F("message") }),
      h(
        "div",
        { class: "fc-body" },
        h(
          "p",
          { class: "fc-pk" },
          h("span", { class: "fc-k" }, "Peak gust"),
          h("b", F("gust")),
          h("span", { class: "fc-t", ...F("gust-time") }),
          h("span", { class: "fc-sq", ...F("squall") }),
        ),
        h(
          "div",
          { class: "fc-lg" },
          h("span", {}, h("i", { class: "k kw" }), "Wind"),
          h("span", {}, h("i", { class: "k kg" }), "Gusts"),
          h("span", {}, h("i", { class: "k kr" }), "Rain chance"),
        ),
        h(
          "svg",
          {
            class: "fc",
            "data-tc-fit": "plot",
            "data-tc-window": "feed:module.wind",
            "aria-hidden": "true",
          },
          parts,
        ),
        h("p", { class: "mo-hon", ...F("honesty") }),
        h("span", { class: "fc-sum", ...F("summary") }),
      ),
    ],
    css: css(`
      .mo-forecast { min-height: 0; }
      .fc-body { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
      .mo-flag[data-state=pending] ~ .fc-body, .mo-flag[data-state=unavailable] ~ .fc-body { display: none; }
      .fc-pk { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; font-size: 12px; color: var(--muted); }
      .fc-pk b { font-size: 15px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }
      .fc-pk b:empty, .fc-pk b:empty ~ span, .fc-t:empty { display: none; }
      .fc-pk b[data-quality=missing] { display: none; }
      .fc-sq { padding: 0 8px; border-radius: 999px; font-weight: 600; }
      .fc-sq:empty { display: none; }
      .fc-sq[data-tone=warn] { color: var(--attention); box-shadow: inset 0 0 0 1px var(--attention); }
      .fc-sq[data-tone=bad] { color: var(--danger); box-shadow: inset 0 0 0 1px var(--danger); }
      .fc-lg { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 14px; font-size: 11.5px; color: var(--muted); margin-top: 6px; }
      .fc-lg span { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
      .fc-lg .k { width: 12px; height: 0; border-top: 2.5px dashed var(--quiet); }
      .fc-lg .kg { border-top: 2px dotted var(--tc-series-3); }
      .fc-lg .kr { width: 8px; height: 8px; border: 0; border-radius: 2.5px; background: var(--muted); opacity: 0.85; }
      .fc { display: block; width: 100%; flex: 1 1 auto; min-height: 130px; overflow: visible; font-family: var(--tc-font-sans); }
      .fc text { font-size: 11px; fill: var(--muted); font-variant-numeric: tabular-nums; }
      .fc .t { text-anchor: end; transform: translate(${M.left - 8}px, calc(${M.top}px + (1 - var(--frac,0)) * ${ph} * 1px + 3.5px)); }
      .fc .g, .fc .b { stroke: var(--grid); stroke-width: 0.5; vector-effect: non-scaling-stroke;
        transform: translate(${M.left}px, calc(${M.top}px + (1 - var(--frac,0)) * ${ph} * 1px)) scaleX(calc(${pw})); }
      .fc .b { stroke-width: 1; transform: translate(${M.left}px, calc(var(--tc-h,168) * 1px - ${M.bottom}px)) scaleX(calc(${pw})); }
      .fc .x { text-anchor: middle; transform: translate(${x("var(--frac,0)")}, calc(var(--tc-h,168) * 1px - ${M.bottom - 15}px)); }
      .fc .l { fill: none; stroke-linejoin: round; }
      .fc .lw { stroke: var(--quiet); stroke-width: 2.5; stroke-dasharray: 3 6; }
      .fc .lg { stroke: var(--tc-series-3); stroke-width: 2; stroke-dasharray: 1 5; stroke-linecap: round; }
      .fc .rn { fill: var(--muted); opacity: 0.85;
        transform: translate(calc(${M.left}px + var(--x,0) * ${pw} * 1px - 1.5px), calc(var(--tc-h,168) * 1px - ${M.bottom}px))
          scale(3, calc(-1 * var(--frac,0) * ${STRIP})); }
      .fc .nw { stroke: var(--grid); stroke-width: 1; vector-effect: non-scaling-stroke;
        transform: translate(${x(n(nowFrac))}, ${M.top}px) scaleY(calc(var(--tc-h,168) - ${M.top + M.bottom})); }
      .fc .nl { font-weight: 600; fill: var(--_c); text-anchor: middle; transform: translate(${x(n(nowFrac))}, ${M.top - 5}px); }
      .fc .pk { fill: var(--tc-series-3); stroke: var(--background); stroke-width: 2;
        transform: translate(${x("var(--x,0)")}, calc(${M.top}px + (1 - var(--y,0)) * ${ph} * 1px)); }
      .fc [data-quality=missing] { display: none; }
      .fc-sum { display: none; }
    `),
  };
}
